import React, { useEffect, useState } from "react";
import { SelfEmployedStep, IndividualEntrepreneurStep, CompanyStep, SelfEmployedLiteStep } from "./steps";
import { FirstStep } from "./first-step";
import {
  useQuery,
  useScrollToErrorElement,
  useSharedDataFromBackend,
} from "~/hooks";
import { OrgTypeEnum, NamesOfFieldEnum } from "~/typings/types";
import { CLIENT_PROMOCODE, ENV_IS_ENABLED_ROISTAT, ENV_IS_ENABLED_YANDEX_METRIKA, ENV_MODE_SELF_EMPLOYED, KEY_CUSTOMER_CLIENT_ID_V2 } from "~/configs/consts";
import { trimUrl } from "~/utils/trimUrl";
import { cleanText, clientApi, getFormattedDate, getFullNameInitDraft, getKeyClientId, isDev, safeLocalStorage, sanitizeData } from "~/utils";
import { CHECK_PAYFORM_EXISTS, DRAFT_SAVE } from "../api/apiUrls";
import { routes } from "~/configs/routes";
import { useGetDraftPreviewId } from "../hooks/useGetDraftPreviewId";
import { FeatureFlagProvider, getFeatureFlagsFromEnv } from "~/components/feature-toggle";
import { AcceptPoliticsProvider } from "~/pages/questionnaires/shared/accept-politics/AcceptPoliticsContext";
import { AnalyticsProvider, mapOrgType, trackEvent } from "~/services/analytics";
import { throttle } from "lodash";
import { clearSentSteps } from "~/services/analytics/utils/sentStepsStorage";
import { LoadingContainer } from "~/containers/loading-сontainer";

const ENV_IS_DISABLED_CHECK_PAYFORM = import.meta.env.VITE_FEATURE_FLAG_is_disabled_check_payform === 'true';
// ключ для флага первого захода.
const INIT_DRAFT_FLAG = "analytics_init_draft_sent";

const configSteps: Record<string, JSX.Element> = {
  [OrgTypeEnum.IndividualEntrepreneur]: <IndividualEntrepreneurStep />,
  [OrgTypeEnum.Company]: <CompanyStep />,
  [OrgTypeEnum.SelfEmployed]: ENV_MODE_SELF_EMPLOYED === 'default' ? <SelfEmployedStep /> : <SelfEmployedLiteStep />,
};

export const V2CustomerQuestionnairePage = () => {
  const [orgType, setOrgType] = useState(OrgTypeEnum.SelfEmployed);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isLoadingFirstFormData, setIsLoadingFirstFormData] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isReturnedFirstStep, setIsReturnedFirstStep] = useState(false);
  const [isRenderSuccessPage, setIsRenderSuccessPage] = useState(false);
  const [isRenderErrorPage, setIsRenderErrorPage] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const [typeRequestCheckPayformName, setTypeRequestCheckPayformName] = useState<any>("init");
  const [errorMsgCheckPayformName, setErrorMsgCheckPayformName] = useState("");
  const query = useQuery();
  const queryPromocode = query.get(CLIENT_PROMOCODE);
  const [isUploadOffer, setIsUploadOffer] = useState(false);
  const [errorUploadOffer, setErrorUploadOffer] = useState(false);
  const [textLinkOffer, setTextLinkOffer] = useState('');
  const [isGeneratedOffer, setIsGeneratedOffer] = useState(false);
  const [isLogoProcessing, setIsLogoProcessing] = useState(false); // отвечатаетза генерацию/удаление/заргузку лого для FieldGenerateLogo

  useScrollToErrorElement();

  const { paymentMethod, paymentMethod_PF2, educationalPlatform, productType } = useSharedDataFromBackend();

  useEffect(() => {
    const itemClientId = safeLocalStorage.getItem(getKeyClientId());
    const storedClientId = itemClientId ? JSON.parse(itemClientId) : null;

    setClientId(cleanText<string>(storedClientId));
  }, [safeLocalStorage.getItem(getKeyClientId())]);

  useEffect(() => {
    if (formData?.offer?.url) {
      setTextLinkOffer(formData.offer.url);
    }
  }, [formData]);

  useGetDraftPreviewId({
    setFormData,
    setOrgType,
    setCurrentStep,
    setIsSubmit,
    setIsReturnedFirstStep,
    isReturnedFirstStep,
    setIsLoadingFirstFormData
  });

  const onResetData = () => {
    setIsSubmit(false);
    setFormData({});
    setCurrentStep(1);

    localStorage.removeItem(KEY_CUSTOMER_CLIENT_ID_V2);
    localStorage.removeItem(INIT_DRAFT_FLAG);
    window.location.href = routes.v2customer;
  };

  const callbackCheckPayformName = async (payformName: any) => {
    try {
      if (ENV_IS_DISABLED_CHECK_PAYFORM) {
        return;
      }
      const payform_name = payformName + ".payform.ru";
      const data = { payform_name: sanitizeData(payform_name) };
      setTypeRequestCheckPayformName("loading");
      setErrorMsgCheckPayformName("");
      const response = await clientApi.post(CHECK_PAYFORM_EXISTS, data);
      const isSuccessResponse = response.data.exists !== 1;
      if (isSuccessResponse) {
        setTypeRequestCheckPayformName("success");
      } else {
        setTypeRequestCheckPayformName("error");
      }
    } catch (error: any) {
      if (error?.code === "ERR_NETWORK" || error?.code === "ERR_BAD_REQUEST") {
        setTypeRequestCheckPayformName("errorValidateFromApi"); // Костыль, надо на беке фиксануть ответ ошибки если падает валидация

        error?.response?.data?.error?.[0]?.message
          && setErrorMsgCheckPayformName(sanitizeData(error?.response?.data?.error?.[0]?.message))
        return;
      }
      setTypeRequestCheckPayformName("error");
    }
  };

  const handleSaveDraft = async () => {
    try {
      if (!clientId) {
        // TODO: add back log why not clientId
        return;
      }
      await clientApi.post(DRAFT_SAVE, {
        id: cleanText(clientId)
      });

      const throttledSendEventAnalitics = throttle(() => {
        trackEvent({
          appVersion: 'default',
          clientId,
          step: currentStep,
          eventName: `anketa-customer-${mapOrgType(orgType)}-sent`,
          extra: { timestamp: Date.now() },
          config: {
            enabled: {
              yandex: ENV_IS_ENABLED_YANDEX_METRIKA,
              roistat: ENV_IS_ENABLED_ROISTAT,
            },
            isDev,
          },
        });
      }, 3000, { leading: true, trailing: false });

      throttledSendEventAnalitics();

      const throttledSendEventAnaliticsWebvisor = throttle(() => {
        trackEvent({
          appVersion: 'default',
          clientId,
          step: currentStep,
          orgType: orgType,
          eventName: `webvisor_anketa_customer_success`,
          extra: { 
            timestamp: Date.now(),
            ui_version: 'v1',
            orgType: orgType,
            step: currentStep,
            eventName: 'webvisor_anketa_customer_success',
            platform: 'desktop',
           },
          config: {
            enabled: {
              yandex: ENV_IS_ENABLED_YANDEX_METRIKA,
              roistat: ENV_IS_ENABLED_ROISTAT,
            },
            isDev,
          },
          providers: ['yandex']
        });
      }, 3000, { leading: true, trailing: false });

      throttledSendEventAnalitics();
      throttledSendEventAnaliticsWebvisor();

      clearSentSteps(); // чистим кеш для аналитики
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const sharedStepProps = {
    initialValues: {
      [NamesOfFieldEnum.IsWantToBecomePartner]: "no",
      [NamesOfFieldEnum.PostalAddressRadio]: formData.flat?.[NamesOfFieldEnum.PostalAddress] && formData.flat?.[NamesOfFieldEnum.PostalAddress] != formData.flat?.[NamesOfFieldEnum.LegalAddress] ? "no" : 'yes',
      [NamesOfFieldEnum.IsVat]: formData.flat?.[NamesOfFieldEnum.Vat] ? formData.flat?.[NamesOfFieldEnum.Vat] === "no" ? "no" : "yes" : undefined,
      [NamesOfFieldEnum.IsNeedExternalIntegrations]: formData.flat?.[NamesOfFieldEnum.IsNeedExternalIntegrations] ? formData.flat?.[NamesOfFieldEnum.IsNeedExternalIntegrations] : 'no',
      [NamesOfFieldEnum.IsNeedRecurrentPayments]: formData.flat?.[NamesOfFieldEnum.IsNeedRecurrentPayments] ? formData.flat?.[NamesOfFieldEnum.IsNeedRecurrentPayments] : 'no',
      [NamesOfFieldEnum.PassportFileId]: [],
      [NamesOfFieldEnum.LogoMainFileId]: [],
      [NamesOfFieldEnum.PassportMainFileId]: [],
      [NamesOfFieldEnum.PassportRegistrationFileId]: [],
      [NamesOfFieldEnum.DocumentAgreement1FileId]: [],
      [NamesOfFieldEnum.DocumentAgreement2FileId]: [],
      [NamesOfFieldEnum.DocumentPricesTariffsFileId]: [],
      ...formData.flat,
      [NamesOfFieldEnum.OrgType]: orgType,
      [NamesOfFieldEnum.CrmName]: getFullNameInitDraft(formData.flat),
      [NamesOfFieldEnum.CrmBirthday]: getFormattedDate(formData.flat?.[NamesOfFieldEnum.CrmBirthday]),
      [NamesOfFieldEnum.LrpProxyDocDate]: getFormattedDate(formData.flat?.[NamesOfFieldEnum.LrpProxyDocDate]),
      [NamesOfFieldEnum.ClientPromocode]:
        (queryPromocode && sanitizeData(cleanText(queryPromocode))) || sanitizeData(formData.flat?.[NamesOfFieldEnum.ClientPromocode]),
      [NamesOfFieldEnum.PayformName]: formData.flat?.[NamesOfFieldEnum.PayformName]
        ? trimUrl(formData.flat[NamesOfFieldEnum.PayformName])
        : "",
      [NamesOfFieldEnum.OfferType]: formData?.offer?.type,
    },
    offer: formData?.offer,
    feature_flags: formData?.feature_flags,
    onSubmit: handleSaveDraft,
    setIsSubmit,
    currentStep,
    setCurrentStep,
    clientId,
    orgType,
    setOrgType,
    setIsReturnedFirstStep,
    onResetData,
    paymentMethod: ENV_MODE_SELF_EMPLOYED === 'lite' && orgType === OrgTypeEnum.SelfEmployed ? paymentMethod_PF2 : paymentMethod,
    educationalPlatform,
    queryPromocode,
    productType,
    isRenderSuccessPage,
    setIsRenderSuccessPage,
    isRenderErrorPage,
    setIsRenderErrorPage,
    typeRequestCheckPayformName,
    setTypeRequestCheckPayformName,
    callbackCheckPayformName,
    errorMsgCheckPayformName,
    isUploadOffer,
    setIsUploadOffer,
    errorUploadOffer,
    setErrorUploadOffer,
    textLinkOffer,
    setTextLinkOffer,
    isGeneratedOffer,
    setIsGeneratedOffer,
    isLogoProcessing,
    setIsLogoProcessing
  };

  return (
    <AnalyticsProvider orgType={orgType} step={currentStep} appVersion={'default'}>
      <LoadingContainer
        isLoading={isLoadingFirstFormData}
        fontSize={48}
      >
        {isSubmit && orgType ? (
          <FeatureFlagProvider
            // flags={formData?.feature_flags || {}}
            flags={getFeatureFlagsFromEnv()}
          >
            <AcceptPoliticsProvider orgType={orgType} clientId={clientId}>
              {React.cloneElement(configSteps[orgType], sharedStepProps)}
            </AcceptPoliticsProvider>
          </FeatureFlagProvider>
        ) : (
          <FirstStep
            orgType={orgType}
            setOrgType={setOrgType}
            setIsSubmit={setIsSubmit}
            setFormData={setFormData}
            onResetData={onResetData}
            clientId={clientId}
          />
        )}
      </LoadingContainer>
    </AnalyticsProvider >
  );
};
